import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {HeadingNode} from '@lexical/rich-text';
import {EditorDoc, EditorDocStatus, EditorDocTemplateType} from 'client';
import {useCollabId, usePlanningId} from 'components/DSYS/CollabProvider';
import {$getRoot} from 'lexical';
import {useCallback, useEffect, useRef} from 'react';
import {usePlanning} from 'views/planning/hooks';
import {isHardcodedTemplate} from 'views/planning/utils';

import {EnhancedHeadingNode} from './EnhancedHeadingNode';
import {getPersistedHeadingCollapsed} from './HeadingCollapsePersistence';
import {useIsTemplateMode} from './RoleContext';

export const shouldCollapseHeading = (
  planning: EditorDoc,
  headingLevel: number,
  headingContext: string
) => {
  const templateType = planning.template_type;
  if (headingLevel !== 1 || !templateType) return false;

  const templateAppliedAt = planning.updated_at ?? planning.created_at;
  const timeSinceTemplateUpdate = Math.max(
    0,
    Date.now() - new Date(templateAppliedAt + 'Z').getTime()
  );
  // Only collapse headings if the doc was recently created or recently re-templated.
  if (timeSinceTemplateUpdate > 5 * 60 * 1000) {
    return false;
  }

  // A doc in the refresh lifecycle stage should collapse parts 1-4.
  if (planning.status === EditorDocStatus.REFRESH) {
    const headingsToCollapse = ['Part 1', 'Part 2', 'Part 3', 'Part 4'];
    for (const heading of headingsToCollapse) {
      if (headingContext.includes(heading)) return true;
    }
    return false;
  }

  // All switchboard-family docs use the standard parts 5 and 6 collapse behavior
  // outside the refresh lifecycle stage.
  if (
    templateType === EditorDocTemplateType.SWITCHBOARD ||
    templateType === EditorDocTemplateType.REFRESH
  ) {
    const headingsToCollapse = ['Part 5', 'Part 6'];
    for (const heading of headingsToCollapse) {
      if (headingContext.includes(heading)) return true;
    }
    return false;
  }

  return false;
};

export const shouldApplyTemplateCollapseDefaults = ({
  isHardcodedTemplateDoc,
  isTemplateMode,
  hasEnteredTemplateMode,
  hasAppliedTemplateDefaults,
  hasPlanning,
}: {
  isHardcodedTemplateDoc: boolean;
  isTemplateMode: boolean;
  hasEnteredTemplateMode: boolean;
  hasAppliedTemplateDefaults: boolean;
  hasPlanning: boolean;
}) =>
  !isHardcodedTemplateDoc &&
  !isTemplateMode &&
  !hasEnteredTemplateMode &&
  !hasAppliedTemplateDefaults &&
  hasPlanning;

/**
 * CollapsePlugin manages the collapse/expand functionality for headings
 * by hiding/showing content under collapsed headings.
 */
export const CollapsePlugin = () => {
  const [editor] = useLexicalComposerContext();
  const planningId = usePlanningId();
  const collabId = useCollabId();
  const {planning} = usePlanning(planningId ?? '');
  const isTemplateMode = useIsTemplateMode();
  // Used to collapse headings by default on first page load
  const hasAppliedTemplateDefaultsRef = useRef(false);
  const hasEnteredTemplateModeRef = useRef(isTemplateMode);
  const templateKey = planning ? `${planning.template_type}:${planning.status}` : undefined;
  const previousTemplateKeyRef = useRef<string | undefined>(templateKey);
  const isTemplate = isHardcodedTemplate(planningId ?? '');

  const updateCollapsedContent = useCallback(() => {
    editor.update(() => {
      const root = $getRoot();
      const children = root.getChildren();

      // First, reset all elements to be visible
      children.forEach((node) => {
        const domElement = editor.getElementByKey(node.getKey());
        if (domElement) {
          domElement.style.display = '';
          domElement.removeAttribute('aria-hidden');
        }
      });

      // Then, apply collapse logic from top to bottom
      children.forEach((node, index) => {
        if (node instanceof EnhancedHeadingNode) {
          const headingLevel = parseInt(node.getTag().replace('h', ''));
          const shouldApplyTemplateDefaults = false;
          const persistedCollapsed = getPersistedHeadingCollapsed(collabId, node.getHeadingId());
          const nextCollapsed =
            persistedCollapsed ??
            (shouldApplyTemplateDefaults && planning
              ? shouldCollapseHeading(planning, headingLevel, node.getTextContent())
              : node.getCollapsed());

          if (
            (persistedCollapsed !== undefined || shouldApplyTemplateDefaults) &&
            node.getCollapsed() !== nextCollapsed
          ) {
            node.setCollapsed(nextCollapsed);
          }

          if (nextCollapsed) {
            // Find all content below this heading until the next heading of same or higher level
            for (let i = index + 1; i < children.length; i++) {
              const nextNode = children[i];

              // If we hit another heading, check if it's same or higher level
              if (nextNode instanceof HeadingNode || nextNode instanceof EnhancedHeadingNode) {
                const nextLevel = parseInt(nextNode.getTag().replace('h', ''));
                if (nextLevel <= headingLevel) {
                  break; // Stop at same or higher level heading
                }
              }

              // Hide this content node
              const domElement = editor.getElementByKey(nextNode.getKey());
              if (domElement) {
                domElement.style.display = 'none';
                domElement.setAttribute('aria-hidden', 'true');
              }
            }
          }
        }
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Existing issue; this suppression was only added to allow existing lint warnings to be upgraded to errors.
  }, [collabId, editor, isTemplate, isTemplateMode, planning]);

  useEffect(() => {
    if (isTemplateMode) {
      hasEnteredTemplateModeRef.current = true;
    }
  }, [isTemplateMode]);

  useEffect(() => {
    if (previousTemplateKeyRef.current !== templateKey) {
      hasAppliedTemplateDefaultsRef.current = false;
      previousTemplateKeyRef.current = templateKey;
    }
  }, [templateKey]);

  useEffect(() => {
    // Initial setup
    updateCollapsedContent();

    // Handle when headings are created or changed
    const removeNodeTransform = editor.registerNodeTransform(EnhancedHeadingNode, () => {
      // Update collapsed content when node changes - use a small delay to ensure state is committed
      setTimeout(() => {
        updateCollapsedContent();
      }, 0);
    });

    // Listen for editor updates - update collapsed content when needed
    const removeUpdateListener = editor.registerUpdateListener(({dirtyElements, dirtyLeaves}) => {
      // Update collapsed content when there are structural changes (including collaboration updates)
      const hasStructuralChanges = dirtyElements.size > 0 || dirtyLeaves.size > 0;

      if (hasStructuralChanges) {
        setTimeout(() => {
          updateCollapsedContent();
          hasAppliedTemplateDefaultsRef.current = true;
        }, 0);
      }
    });

    return () => {
      removeNodeTransform();
      removeUpdateListener();
    };
  }, [editor, updateCollapsedContent]);

  return null; // This plugin doesn't render anything
};
